/**
 * Owner: Masa Bayu
 * File: demo-author-analytics.ts
 * Date:Time: 2026-09-16 15:10
 * Description: Demo SaleRecord → AuthorAnalyticsSlice for T6-E6 UI.
 */

import {
  confirmOrderAfterSuccessfulPayment,
  createAuthorCapableAccount,
  createPendingOrderDraft,
  createPaymentAttempt,
  createPaymentIntent,
  createQxreadAuthorAccount,
  createQxreadSession,
  projectAuthorAnalyticsSlice,
  projectAuthorAnalyticsSummary,
  projectSaleRecordsFromConfirmedOrders,
  type QxreadAuthorAnalyticsSummary,
  type QxreadCheckoutSession,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_author';
const DEMO_NOW = '2026-09-16T07:10:00.000Z';
const DEMO_PUB = 'qxrdpub_demo_store';

function demoCheckout(): QxreadCheckoutSession {
  return Object.freeze({
    checkoutId: 'qxrdchk_demo_anly',
    cartId: 'qxrdcart_demo_anly',
    sessionKey: 'qxrdsess_demo_anly',
    state: 'ready' as const,
    totals: Object.freeze({
      currency: 'USD',
      sellerSubtotalMinorUnits: 1299,
      serviceFeeMinorUnits: 58,
      buyerTotalMinorUnits: 1357,
      affiliateContractMinorUnits: 65,
      referralContractMinorUnits: 65,
      rbsContractMinorUnits: 0,
      uniquePublicationCount: 1,
      ledgerLines: Object.freeze([]),
      attributions: Object.freeze([]),
    }),
    blockers: Object.freeze([] as string[]),
    createdAt: DEMO_NOW,
  });
}

let demoAccount = createAuthorCapableAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
});
let demoSession = createQxreadSession({
  state: 'authenticated',
  accountId: DEMO_ACCOUNT_ID,
  issuedAt: DEMO_NOW,
});
let demoAuthor = createQxreadAuthorAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
  slug: 'demo-author',
  createdAt: DEMO_NOW,
  updatedAt: DEMO_NOW,
});
let demoSummary: QxreadAuthorAnalyticsSummary | null = null;

function ensureActors(): void {
  demoAccount = createAuthorCapableAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
  });
  demoSession = createQxreadSession({
    state: 'authenticated',
    accountId: DEMO_ACCOUNT_ID,
    issuedAt: DEMO_NOW,
  });
  demoAuthor = createQxreadAuthorAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
    slug: 'demo-author',
    createdAt: DEMO_NOW,
    updatedAt: DEMO_NOW,
  });
}

export function resetDemoAuthorAnalytics(): void {
  ensureActors();
  demoSummary = null;
}

export function getDemoAnalyticsSummary(): QxreadAuthorAnalyticsSummary | null {
  return demoSummary;
}

export function projectDemoAuthorAnalytics(): string {
  const pending = createPendingOrderDraft(demoCheckout(), [
    {
      publicationId: DEMO_PUB,
      title: 'River Atlas',
      quantity: 2,
      sellerPriceMinorUnits: 1299,
      currency: 'USD',
    },
  ]);
  const intent = createPaymentIntent(pending, { idempotencyKey: 'demo_anly_pay' });
  const attempt = createPaymentAttempt(intent);
  const succeeded = Object.freeze({
    ...attempt,
    state: 'succeeded' as const,
    updatedAt: DEMO_NOW,
  });
  const confirmed = confirmOrderAfterSuccessfulPayment(pending, succeeded, {
    confirmedAt: DEMO_NOW,
  });
  const sales = projectSaleRecordsFromConfirmedOrders({
    authorAccount: demoAuthor,
    authorOwnedPublicationIds: new Set([DEMO_PUB]),
    confirmedOrders: [confirmed],
    recordedAt: DEMO_NOW,
  });
  const slice = projectAuthorAnalyticsSlice({
    authorAccount: demoAuthor,
    saleRecords: sales,
    audience: { followerCount: 128, profileViews: 2400, asOf: DEMO_NOW },
    periodLabel: '30d',
    projectedAt: DEMO_NOW,
  });
  demoSummary = projectAuthorAnalyticsSummary({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    slice,
    projectedAt: DEMO_NOW,
  });
  return `Analytics projected · units=${String(demoSummary.slice.totalUnitsSold)} · includesRawReaderTelemetry=false · inventedByAuthorUi=false`;
}
